import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { Button, FocusTrap, Paper, Text } from "@mantine/core";
import {
  computeTooltipStyle,
  getSpotlightClipPath,
  type Rect,
  type TutorialPlacement,
  type Viewport,
} from "./tutorial-geometry";
import { resolveAnchor, type Measure } from "./tutorial-anchor";
import { isTypingTarget } from "@/hooks/useCoverageBetaHotkeys";

/**
 * One step of the Coverage Beta onboarding tutorial. Forked from the classic
 * Coverage walkthrough's `TrainingStep`
 * (components/coverage/CoverageTrainingOverlay.tsx) with two changes:
 *  - `id`: a stable key for `onStepChange`, so a host mapping steps to app
 *    state doesn't desync when a step is inserted — classic keys its
 *    `switch` off the raw array index instead.
 *  - `anchors`: an ORDERED FALLBACK CHAIN (most-specific first), not a single
 *    selector plus one fallback. Some Beta anchors need to degrade through
 *    more than one region (e.g. shelf-images-view -> right-panel-body ->
 *    right-panel) — see tutorial-anchor.ts's `resolveAnchor`.
 */
export interface TrainingStep {
  id: string;
  title: string;
  description: ReactNode;
  /**
   * `data-cb-tour` values to try, most-specific first. The first one that
   * resolves to a usable rect (tutorial-anchor.ts) is spotlighted; an empty
   * array always centers the step with no spotlight (e.g. the welcome step,
   * or a step illustrating something with no single on-screen home, like the
   * confirm-product panel).
   */
  anchors: readonly string[];
  placement: TutorialPlacement;
}

/** Above every task modal (Skip/PlanogramSearch/QuizMetrics at 10000,
 * ConfidenceModal at 10001) so a stray dialog can't sit on top of the tour;
 * below the two session-integrity modals (IdleWarningModal 10100, ExitModal
 * 10200 — both from @labeling-tools/ui-components), which must always win
 * since they're about losing the task assignment, not about onboarding. */
const TOUR_Z_INDEX = 10050;

/** Query the DOM for one anchor's element and return its rect, or null if it isn't mounted. */
const measureAnchor: Measure = (anchor) => {
  const el = document.querySelector<HTMLElement>(`[data-cb-tour="${anchor}"]`);
  return el ? el.getBoundingClientRect() : null;
};

const getViewport = (): Viewport => ({
  width: window.innerWidth,
  height: window.innerHeight,
});

/** Smallest rect delta (px) worth a re-render — avoids a setState on every
 * animation frame when the spotlighted element hasn't actually moved. */
const RECT_EPSILON = 0.5;

const rectsEqual = (a: Rect | null, b: Rect | null): boolean => {
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    Math.abs(a.top - b.top) < RECT_EPSILON &&
    Math.abs(a.left - b.left) < RECT_EPSILON &&
    Math.abs(a.right - b.right) < RECT_EPSILON &&
    Math.abs(a.bottom - b.bottom) < RECT_EPSILON
  );
};

export interface CoverageBetaTrainingOverlayProps {
  active: boolean;
  steps: readonly TrainingStep[];
  /** Fired on every terminal dismissal (Done on the last step, Skip, or Escape) — never on nothing (there is always a `lastStep`). */
  onClose: (result: { completed: boolean; lastStep: number }) => void;
  /** Fired once on open (for the first step) and again on every Next/Back — lets the host drive app state (e.g. switch the right panel) so a step's anchor actually exists to spotlight. */
  onStepChange?: (step: TrainingStep) => void;
}

/**
 * The onboarding tutorial's presentational engine: a dimmed scrim with a
 * spotlight cutout over the active step's resolved anchor, plus a tooltip
 * card with Skip / Back / Next controls. Forked from classic Coverage's
 * `CoverageTrainingOverlay` with its three known issues fixed:
 *  - Persists on every exit (Done, Skip, Escape), not only when a "don't
 *    show again" box is ticked (there is no such box here).
 *  - Escape is a first-class close path (handled by this component itself,
 *    not bypassed by the host), so it always fires `onClose`.
 *  - Always resets to the first step when re-opened, so the replay button
 *    can't reopen mid-tour.
 * It is otherwise app-agnostic: it knows nothing about Coverage Beta's
 * store, hotkeys, or task lifecycle — see `CoverageBetaTraining` for that.
 */
export const CoverageBetaTrainingOverlay = (
  props: CoverageBetaTrainingOverlayProps,
) => {
  const { active, steps, onClose, onStepChange } = props;
  const [activeStep, setActiveStep] = useState(0);
  const [spotlightRect, setSpotlightRect] = useState<Rect | null>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const nextButtonRef = useRef<HTMLButtonElement>(null);
  const openerRef = useRef<HTMLElement | null>(null);
  const rafRef = useRef<number>(0);
  const [, forceUpdate] = useState(0);

  const step = steps[activeStep];
  const isFirst = activeStep === 0;
  const isLast = activeStep === steps.length - 1;

  // On each fresh open: reset to step 0 (classic doesn't, so its "?" button
  // can reopen mid-tour), capture what had focus to restore on close, and
  // fire onStepChange for the first step. The ref guard (not just `active`
  // in the deps) keeps this to once per open even if `steps`/`onStepChange`
  // change identity while open.
  const mountedRef = useRef(false);
  useEffect(() => {
    if (active && !mountedRef.current) {
      mountedRef.current = true;
      setActiveStep(0);
      openerRef.current = document.activeElement as HTMLElement | null;
      const firstStep = steps[0];
      if (firstStep) onStepChange?.(firstStep);
    }
    if (!active) {
      mountedRef.current = false;
    }
  }, [active, steps, onStepChange]);

  // Re-render once after the tooltip mounts/changes so computeTooltipStyle's
  // clamping uses its real measured size instead of the pre-measurement
  // fallback (mirrors classic's forceUpdate trick).
  useLayoutEffect(() => {
    if (tooltipRef.current) forceUpdate((n) => n + 1);
  }, [activeStep]);

  // Focus the primary action on open and on every step change; restore
  // whatever had focus before once the tour closes. Load-bearing, not just
  // politeness: the app's own Tab/Shift hotkeys are suppressed while the
  // tour is open (see useCoverageBetaHotkeys), so a focus trap is the only
  // way to keep Tab cycling among Skip/Back/Next instead of doing nothing.
  useEffect(() => {
    if (!active) return;
    nextButtonRef.current?.focus();
  }, [active, activeStep]);

  useEffect(() => {
    if (active) return;
    openerRef.current?.focus?.();
  }, [active]);

  // Continuously re-resolve the active step's anchor while the tour is open.
  // A plain resize/scroll listener isn't enough here: react-zoom-pan-pinch
  // writes pan/zoom transforms straight to the DOM outside React (no resize
  // ever fires for that), and the sidebar's scroller is `<main>`
  // (Layout.tsx), not `window`. A polling loop subsumes both for free, plus
  // image-load and layout-settling delays, at the cost of one cheap rect
  // read per frame while the labeler is reading, not labeling.
  useEffect(() => {
    if (!active) return;

    const tick = () => {
      const nextRect = step
        ? resolveAnchor(step.anchors, measureAnchor, getViewport())
        : null;
      setSpotlightRect((prev) =>
        rectsEqual(prev, nextRect) ? prev : nextRect,
      );
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [active, step]);

  const finish = useCallback(
    (completed: boolean) => {
      onClose({ completed, lastStep: activeStep });
    },
    [onClose, activeStep],
  );

  // Side effects (finish/onStepChange) live in the handler body, not inside
  // setActiveStep's updater — updater functions must be pure, and this app
  // runs in StrictMode, which deliberately double-invokes them in dev to
  // catch exactly this: an impure updater here would fire onClose (writing
  // to localStorage) or onStepChange twice per click.
  const handleNext = useCallback(() => {
    if (activeStep >= steps.length - 1) {
      finish(true);
      return;
    }
    const next = activeStep + 1;
    setActiveStep(next);
    const nextStep = steps[next];
    if (nextStep) onStepChange?.(nextStep);
  }, [activeStep, steps, finish, onStepChange]);

  const handleBack = useCallback(() => {
    if (activeStep <= 0) return;
    const prev = activeStep - 1;
    setActiveStep(prev);
    const prevStep = steps[prev];
    if (prevStep) onStepChange?.(prevStep);
  }, [activeStep, steps, onStepChange]);

  const handleSkip = useCallback(() => finish(false), [finish]);

  // Own keyboard navigation. There is nothing to fight over with the app's
  // hotkeys: useCoverageBetaHotkeys bails out entirely while
  // store.tutorialActive is true, so registration order between the two
  // `window` keydown listeners never matters.
  useEffect(() => {
    if (!active) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (isTypingTarget(e.target)) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      switch (e.key) {
        case "Escape": {
          e.preventDefault();
          finish(false);
          break;
        }
        case "ArrowRight":
        case "Enter":
        case " ": {
          e.preventDefault();
          handleNext();
          break;
        }
        case "ArrowLeft": {
          e.preventDefault();
          handleBack();
          break;
        }
        default:
          break;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [active, finish, handleNext, handleBack]);

  if (!active || !step) return null;

  const hasSpotlight = step.anchors.length > 0 && spotlightRect !== null;
  const tooltipSize = {
    width: tooltipRef.current?.offsetWidth ?? 0,
    height: tooltipRef.current?.offsetHeight ?? 0,
  };

  return createPortal(
    <div className="fixed inset-0" style={{ zIndex: TOUR_Z_INDEX }}>
      <div
        className="absolute inset-0"
        style={{
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          ...(hasSpotlight
            ? { clipPath: getSpotlightClipPath(spotlightRect as Rect) }
            : {}),
        }}
        aria-hidden="true"
        onClick={(e) => e.stopPropagation()}
      />

      <FocusTrap active={active}>
        <div>
          <Paper
            ref={tooltipRef}
            shadow="lg"
            p="md"
            radius="md"
            role="dialog"
            aria-modal="true"
            aria-labelledby="cb-tutorial-title"
            aria-describedby="cb-tutorial-description"
            style={{
              ...computeTooltipStyle(
                step.placement,
                hasSpotlight ? spotlightRect : null,
                tooltipSize,
                getViewport(),
              ),
              zIndex: TOUR_Z_INDEX + 1,
            }}
          >
            <Text id="cb-tutorial-title" fw={700} size="md" mb={4}>
              {step.title}
            </Text>
            <div
              id="cb-tutorial-description"
              className="text-sm text-gray-500 mb-3"
              style={{ whiteSpace: "pre-line" }}
              aria-live="polite"
            >
              {step.description}
            </div>
            <Text size="xs" c="dimmed" mb="sm">
              Step {activeStep + 1} of {steps.length}
            </Text>

            <div className="flex items-center justify-between gap-2">
              <Button variant="subtle" size="xs" onClick={handleSkip}>
                Skip tour
              </Button>
              <div className="flex items-center gap-2">
                {!isFirst && (
                  <Button variant="default" size="xs" onClick={handleBack}>
                    Back
                  </Button>
                )}
                <Button ref={nextButtonRef} size="xs" onClick={handleNext}>
                  {isLast ? "Done" : "Next"}
                </Button>
              </div>
            </div>
          </Paper>
        </div>
      </FocusTrap>
    </div>,
    document.body,
  );
};
